import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { publicApi } from "@/lib/publicApi";
import {
  Bus, ShieldCheck, MapTrifold, Ticket, CreditCard, Lightning,
  Lifebuoy, ChartLineUp, ArrowRight, Globe, Phone, EnvelopeSimple,
} from "@phosphor-icons/react";

const TID = {
  root: "landing-root",
  ctaCitizen: "cta-citizen-portal",
  ctaAdmin: "cta-admin-portal",
};

export default function Landing() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { publicApi.get("/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  return (
    <div data-testid={TID.root} className="min-h-dvh bg-[#0A0F1F] text-white overflow-x-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }} />

      {/* Top nav */}
      <header className="relative z-10 px-5 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center font-bold text-slate-900">K</div>
          <div className="leading-tight">
            <div className="font-bold text-base">KTCL</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Goa · Public Transport</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <a href="https://ktclgoa.com" target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-300 hover:text-white transition-colors">
            <Globe size={14}/> ktclgoa.com
          </a>
          <button data-testid={TID.ctaAdmin} onClick={() => navigate("/login")}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md bg-white/10 hover:bg-white/20 text-xs sm:text-sm font-semibold border border-white/15 transition-colors">
            <ShieldCheck size={15}/> Admin
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-5 lg:px-10 pt-8 lg:pt-16 pb-12 lg:pb-20 max-w-6xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-semibold mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/> Live demo · Goa fleet running on simulated GPS
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
          Goa moves smarter <br/> with <span className="bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent">KTCL ITMS</span>.
        </h1>
        <p className="mt-5 text-slate-300 text-base lg:text-lg max-w-2xl leading-relaxed">
          A single platform powering Kadamba Transport Corporation: live tracking, dispatch, schedules, passes, QR ticketing — and a citizen webapp passengers actually love.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button data-testid={TID.ctaCitizen} onClick={() => navigate("/majhe")}
            className="group flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-900 font-bold text-sm sm:text-base shadow-lg shadow-emerald-500/25 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-slate-900/15 flex items-center justify-center"><Bus size={20} weight="bold"/></div>
              <div className="text-left">
                <div className="text-[11px] uppercase tracking-wider opacity-70">For Passengers</div>
                <div>Open MajheApp × KTCL</div>
              </div>
            </div>
            <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform"/>
          </button>
          <button onClick={() => navigate("/login")}
            className="group flex items-center justify-between gap-3 px-5 py-3.5 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold text-sm sm:text-base border border-white/10 transition-all">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center"><ShieldCheck size={20} weight="bold"/></div>
              <div className="text-left">
                <div className="text-[11px] uppercase tracking-wider opacity-60">For Operations</div>
                <div>Admin Control Center</div>
              </div>
            </div>
            <ArrowRight size={20} weight="bold" className="opacity-60 group-hover:translate-x-1 transition-transform"/>
          </button>
        </div>

        {/* Live stats strip */}
        {stats && (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { l: "Buses in fleet", v: stats.buses_total, accent: "text-emerald-300" },
              { l: "Active now", v: stats.buses_active, accent: "text-cyan-300" },
              { l: "Routes", v: stats.routes, accent: "text-amber-300" },
              { l: "Bus stops", v: stats.stops.toLocaleString(), accent: "text-fuchsia-300" },
              { l: "Passes issued", v: stats.passes_issued, accent: "text-emerald-300" },
              { l: "Smart Cards", v: stats.cards_issued, accent: "text-cyan-300" },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 backdrop-blur-sm">
                <div className={`font-display text-2xl lg:text-3xl font-bold ${s.accent}`}>{s.v}</div>
                <div className="text-[11px] text-slate-400 uppercase tracking-wide mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Two-pillar showcase */}
      <section className="relative z-10 px-5 lg:px-10 pb-14 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Citizen pillar */}
          <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent border border-emerald-500/20 p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded bg-emerald-500/30 text-emerald-200">Citizen Webapp</span>
              <span className="text-[11px] text-slate-400">majhektcl.gov.in</span>
            </div>
            <h3 className="font-display text-2xl font-bold mb-2">For 1.5M Goa passengers</h3>
            <p className="text-sm text-slate-300 mb-5 leading-relaxed">
              Find buses, plan routes, buy QR tickets, apply for passes, top up cards — all from a phone. Works offline-friendly inside MajheApp webview.
            </p>
            <ul className="space-y-2 mb-6">
              {[
                { i: MapTrifold, t: "Live bus tracking on Goa map" },
                { i: Ticket, t: "QR ticket buy & validate" },
                { i: CreditCard, t: "Smart Transit Card recharge" },
                { i: Lifebuoy, t: "Service alerts & route diversions" },
              ].map((f) => (
                <li key={f.t} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <f.i size={16} className="text-emerald-300 shrink-0"/> {f.t}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate("/majhe")}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm transition-colors">
              Open MajheApp Webapp <ArrowRight size={16} weight="bold"/>
            </button>
          </div>

          {/* Admin pillar */}
          <div className="rounded-2xl bg-gradient-to-br from-indigo-500/10 via-indigo-500/5 to-transparent border border-indigo-500/20 p-6 lg:p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded bg-indigo-500/30 text-indigo-200">Admin Console</span>
              <span className="text-[11px] text-slate-400">Internal use</span>
            </div>
            <h3 className="font-display text-2xl font-bold mb-2">Run the entire corporation</h3>
            <p className="text-sm text-slate-300 mb-5 leading-relaxed">
              19 modules covering live dispatch, schedules, crew, fleet, EVs, private operator settlements, passes, smart cards, reports & open data feeds.
            </p>
            <ul className="space-y-2 mb-6">
              {[
                { i: Lightning, t: "Real-time live map + dispatch board" },
                { i: ChartLineUp, t: "Schedules, master schedules, OSRM routing" },
                { i: ShieldCheck, t: "RBAC matrix + audit logs" },
                { i: Globe, t: "GTFS open-data for Google Maps & Moovit" },
              ].map((f) => (
                <li key={f.t} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <f.i size={16} className="text-indigo-300 shrink-0"/> {f.t}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate("/login")}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold text-sm border border-white/15 transition-colors">
              Sign in to Admin <ArrowRight size={16} weight="bold"/>
            </button>
            <div className="mt-3 text-[11px] text-slate-400">Demo: admin@ktcl.gov.in / admin123</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-5 lg:px-10 py-6 border-t border-white/5 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-400">
          <div>© 2026 Kadamba Transport Corporation Limited · Govt. of Goa</div>
          <div className="flex items-center gap-4">
            <a href="tel:+918322438034" className="flex items-center gap-1.5 hover:text-white"><Phone size={12}/> 0832-2438034</a>
            <a href="mailto:info@ktclgoa.com" className="flex items-center gap-1.5 hover:text-white"><EnvelopeSimple size={12}/> info@ktclgoa.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
